import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { secureGet, GetExcel } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import PageScroller from "../../components/PageScroller";
import Filter from "../../components/Filter";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import Rows from "../../components/Rows";
import { saveAs } from "file-saver";

import cloudIcon from "../../icons/download-cloud.svg";

const RaportEcarts = () => {
  const { t } = useTranslation();
  const topRef = useRef(null);

  const [rapportEcarts, setRapportEcarts] = useState([]);
  const [inventories, setInventories] = useState([]);

  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [filters, setFilters] = useState({ needle: "", inventory: ""});

  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const showSnackbar = (message, severity = "success", manual = false) =>
    setSnackbar({ open: true, message, severity, manual });


  const filterOptions = [
    { name: "needle", type: "search" },
    {
      name: "inventory",
      type: "dropdown",
      label: t("fields.inventory.inventaire"),
      options: inventories.map((r) => ({ value: String(r.id), label: r.name })),
    },
  ];

  const columns = [
    {
      key: "inventoryName",
      label: t("fields.inventory.inventaire"),
      width: "15%",
    },
    {
      key: "reference",
      label: t("options.reference"),
      width: "15%",
    },
    {
      key: "name",
      label: t("options.name"),
      width: "20%",
    },
    {
      key: "initialQty",
      label: t("fields.inventory.initialQty"),
      width: "15%",
    },
    {
      key: "countedQty",
      label: t("fields.inventory.countedQty"),
      width: "15%",
    },
    {
      key: "ecart",
      label: t("fields.inventory.ecart"),
      width: "10%",
      value: (row) => row.initialQty - row.countedQty > 0 ? "+" + (row.initialQty - row.countedQty) : (row.initialQty - row.countedQty),
    },
    {
      key: "amount",
      label: t("fields.inventory.value"),
      width: "15%",
    },
  ];

  const pageRows = (data) => (Array.isArray(data) ? data : data?.content ?? []);
  const toInt = (n, fallback) => (Number.isFinite(n) ? n : fallback);

  const fetchRapportEcarts = useCallback(
    async (page = pageNumber, size = pageSize, active = filters) => {
      console.log(active);
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("size", String(size));

        const n = (active.needle || "").trim();
        if (n) params.set("needle", n);

        const inventoryId = active.inventory;

        const res = await secureGet(`/inventories/${inventoryId}/ecarts?${params.toString()}`, showSnackbar, { signal });
        console.log(res);
        const pageData = res?.data ?? {};
        const content = pageRows(pageData);

        setRapportEcarts(content);
        setTotalPages(toInt(pageData.totalPages, 1));
        if (Number.isFinite(pageData.number)) setPageNumber(pageData.number);
        if (Number.isFinite(pageData.size)) setPageSize(pageData.size);
      } catch (error) {
        if (error.name !== "AbortError") console.error("Error fetching rapport ecarts:", error);
      } finally {
        setLoading(false);
      }
    },
    [filters, pageNumber, pageSize, signal]
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchData = async () => {
      try {
        const r = await secureGet("/inventories?size=1000", showSnackbar, { signal });
        const inventoriesData = pageRows(r?.data);
        setInventories(inventoriesData);
        console.log("inventoriesData", inventoriesData);
        const reset = { needle: "", inventory: inventoriesData[0].id};
        setFilters(reset);
        setPageNumber(0);
        fetchRapportEcarts(0, pageSize, reset);
      } catch (e) {
        if (e.name !== "AbortError") console.error(e);
      }
    };

    fetchData();
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = async (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    setPageNumber(0);
    
    if (name === "inventory") {
      const inventoryId = parseInt(value, 10);
      console.log("Looking for inventory with id:", inventoryId, "in", inventories);
      const selectedInventory = inventories.find(i => i.id === inventoryId);
      console.log("selectedInventory", selectedInventory);
    }
    
    fetchRapportEcarts(0, pageSize, updated);
  };

  const resetFilters = async () => {
    const reset = { needle: "", inventory: inventories[0].id};
    setFilters(reset);
    setPageNumber(0);
    fetchRapportEcarts(0, pageSize, reset);
  };

  const handlePageChange = (p) => {
    setPageNumber(p);
    fetchRapportEcarts(p, pageSize, filters);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (n) => {
    const sizeNum = Number(n) || pageSize;
    setPageSize(sizeNum);
    setPageNumber(0);
    fetchRapportEcarts(0, sizeNum, filters);
  };

  const handleDownload = async () => {
    const inventoryId = (filters?.inventory || "").trim();
    if (!inventoryId) {
      showSnackbar(t("errors.selectInventoryFirst"), "error");
      return;
    }
    try {
      const response = await GetExcel(`/inventories/${inventoryId}/final-stock/export`, showSnackbar);
      const today = new Date().toISOString().split("T")[0];
      const fileName = `Rapport_${inventoryId}_${today}.xlsx`;
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, fileName);
      showSnackbar(t("confirmation.downloadSuccess"), "success");
    } catch (error) {
      console.error("Error downloading ecarts report:", error);
      showSnackbar(t("errors.generic"), "error");
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.ecartsReport")}</title>
      </Helmet>

      {/* Header */}
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t("titles.ecartsReport")}</h1>
      </div>

      <div className="flex items-end gap-3 mb-[10px]">
        <div className="flex-1">
          <Filter
            filters={filterOptions}
            onFilterChange={handleFilterChange}
            resetFilters={resetFilters}
            filterValues={filters}
          />
        </div>

        <button
          onClick={handleDownload}
          className="h-[44px] w-[44px] rounded-[12px] bg-white shadow-[0px_20px_80px_#00000011]
           transition grid place-items-center flex-shrink-0"
        >
          <img src={cloudIcon} alt="download" className="w-6 h-6" />
        </button>
      </div>


      {/* Content */}
      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        )}

        <Rows
          columns={columns}
          data={rapportEcarts}
        />
      </div>

      {/* Pagination */}
      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Snackbar */}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default RaportEcarts;
